import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import type { BulkImportError, ErrorResponse } from "../api";

import { Section } from "../ui/atoms/section";
import { BulkImportErrorMessage } from "../ui/molecules/bulk-import-error-message";
import { Icon, MessageBar, MessageBarButton, MessageBarType } from "@fluentui/react";
import css from "./page-bulk-import.scss";
import { defaultPageSize } from "../utils/constants";
import { store } from "../store";
import { FusionLayout } from "@/parts/fusion-layout";

export function PageBulkImport(): JSX.Element {
    const { t } = useTranslation();
    const [error, setError] = useState<BulkImportError | undefined>(undefined);
    /** Set on a successful file upload */
    const [success, setSuccess] = useState(false);
    /** Set on a successful file validation */
    const [validatedFile, setValidatedFile] = useState<ArrayBuffer | undefined>(undefined);

    const sitesRepository = store.repositorySites;
    const departmentsRepository = store.repositoryDepartments;
    const preOpsChecklistsRepository = store.repositoryPreOpsChecklists;
    const vehiclesRepository = store.repositoryVehicles;
    const vehicleGroupsRepository = store.repositoryVehicleGroups;
    const usersRepository = store.repositoryUsers;
    const userGroupsRepository = store.repositoryUserGroups;
    const publicSettingsRepository = store.repositoryPublicSettings;
    const vehicleTypesRepository = store.repositoryVehicleTypes;
    const safetyMessagesRepository = store.repositorySafetyMessages;

    /** Constructs the bulk import request. */
    function getBulkImportRequest(file: ArrayBuffer, path?: string): Request {
        // The generated OpenAPI spec odes not work for uploading the file. Therefore
        // we build the request manually.
        let url = "/api/v1/bulk_import";
        if (path) {
            url += path;
        }
        const request = new Request(url, {
            method: "POST",
            credentials: "same-origin",
            body: new Uint8Array(file),
            headers: [
                [
                    "Content-Type",
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
                ],
            ],
        });
        return request;
    }

    /** Reads the input file. */
    function readFile(event: React.ChangeEvent<HTMLInputElement>): void {
        setSuccess(false);
        // Return when no files are selected
        if (!event.target.files) {
            return;
        }

        // Read file
        const reader = new FileReader();
        reader.onload = (e) => {
            const res = e.target?.result as ArrayBuffer;
            // Validate file on a finished read
            validateFile(res);
        };
        const file = event.target.files[0];
        reader.readAsArrayBuffer(file);
    }

    /** Validates the input file via the backend validation endpoint. */
    async function validateFile(file: ArrayBuffer): Promise<void> {
        if (!file) {
            return;
        }
        const request = getBulkImportRequest(file, "/validate");
        const response = await fetch(request);

        // On OK, return and set valid file
        if (response.ok) {
            setError(undefined);
            setValidatedFile(file);
            return;
        }

        // Otherwise we received an error
        const error = (await response.json()) as ErrorResponse;
        if (error.error.bulkImportError) {
            setError(error.error.bulkImportError);
            setValidatedFile(undefined);
        }
    }

    /** Uploads a valid file to the backend. */
    async function uploadFile(): Promise<void> {
        if (!validatedFile) {
            return;
        }
        const request = getBulkImportRequest(validatedFile);
        const response = await fetch(request);
        if (response.ok) {
            setSuccess(true);
            setValidatedFile(undefined);
            await resetRepositories();
            return;
        }
    }

    /** Resets all repositories affected by the bulk import. Otherwise
        we could end up displaying outdated data due to the mobx
        caching problem. */
    async function resetRepositories(): Promise<void> {
        await sitesRepository.reloadQuery({ pageSize: defaultPageSize });
        await departmentsRepository.reloadQuery({ pageSize: defaultPageSize });
        await preOpsChecklistsRepository.reloadQuery({ pageSize: defaultPageSize });
        await vehiclesRepository.reloadQuery({ pageSize: defaultPageSize });
        await vehicleGroupsRepository.reloadQuery({ pageSize: defaultPageSize });
        await usersRepository.reloadQuery({ pageSize: defaultPageSize });
        await userGroupsRepository.reloadQuery({ pageSize: defaultPageSize });
        await publicSettingsRepository.reloadQuery();
        await vehicleTypesRepository.reloadQuery({ pageSize: defaultPageSize });
        await safetyMessagesRepository.reloadQuery({ pageSize: defaultPageSize });
    }

    return (
        <FusionLayout breadcrumb={t("page.bulkImport.title")} classic>
            <Section withPadding title={t("page.bulkImport.title")}>
                <BulkImportErrorMessage error={error} />
                {success ? (
                    <>
                        <MessageBar messageBarType={MessageBarType.success}>
                            {"That worked! All data from your sheet was successfully imported."}
                        </MessageBar>
                    </>
                ) : (
                    <></>
                )}
                {validatedFile ? (
                    <MessageBar
                        isMultiline={false}
                        messageBarType={MessageBarType.info}
                        actions={
                            <MessageBarButton onClick={() => uploadFile()}>
                                {"Import data"}
                            </MessageBarButton>
                        }
                    >
                        {
                            "Your input file contains valid data! Execute your import by clicking the 'Import data' button."
                        }
                    </MessageBar>
                ) : (
                    <></>
                )}
                <br />
                <label htmlFor="file-upload" className={css.pageBulkImport__label}>
                    <Icon iconName="OpenFile" className={css.pageBulkImport__icon} />
                    <p>{"Drag and drop a file here"}</p>
                </label>
                <br />
                <input id="file-upload" type="file" accept=".xlsx" onChange={readFile} />
            </Section>
        </FusionLayout>
    );
}
